/**
 * @module Views/AdminDashboard/CommissionReportsPage
 * @description This module renders the Commission Reports page, enabling users to view and manage commission reports.
 * It includes filtering, pagination, and downloading functionality.
 * @returns {React.Component} The rendered CommissionReportsPage component.
 */

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { fetchReportBlob, downloadBlob } from 'services/reportService';
import axios from 'axios';
import { ContainedButton1 } from 'components/DesignSystem/Button';
import CustomDatePicker from 'components/DesignSystem/CustomDatePicker';
import { Dropdown } from 'components/DesignSystem/DropDown';
import useSwalAlert from 'components/DesignSystem/SwalAlert';
import { CustomTableCell, CustomTablePagination, CustomTableRow } from 'components/DesignSystem/Table/CustomTable';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { filter } from 'lodash';
import Scrollbar from 'oldLayouts/components/scrollbar';
import { UserListHead, UserListToolbar } from 'oldLayouts/components/user';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import PageContainer from 'views/dashboard/PageContainer';
import * as Yup from 'yup';

const COMMISSION_TABLE_HEAD = [
  { id: 'partnerName', label: 'Partner Name', alignRight: false },
  { id: 'partnerCode', label: 'Partner Code', alignRight: false },
  { id: 'fromDate', label: 'From Date', alignRight: false },
  { id: 'toDate', label: 'To Date', alignRight: false },
  { id: 'totalDiscount', label: 'Total Discount', alignRight: false },
  { id: 'totalAmount', label: 'Total Amount', alignRight: false },
  { id: 'totalCommission', label: 'Total Commission', alignRight: false },
  { id: 'action', label: 'Action', alignCenter: true },
];

/**
 * Compares two objects in descending order based on the specified property.
 *
 * @param {Object} a - The first object to compare.
 * @param {Object} b - The second object to compare.
 * @param {string} orderBy - The property name to compare.
 * @returns {number} - Returns -1 if `b[orderBy]` is less than `a[orderBy]`,
 * 1 if `b[orderBy]` is greater than `a[orderBy]`, or 0 if they are equal.
 */
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

/**
 * Returns a comparator function to sort an array in ascending or descending order.
 *
 * @param {'asc'|'desc'} order - The order of sorting ('asc' for ascending, 'desc' for descending).
 * @param {string} orderBy - The property name to sort by.
 * @returns {Function} - A comparator function that takes two arguments and returns a sorting order.
 */
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * Sorts and filters an array based on a comparator function and a search query.
 *
 * @param {Array<Object>} array - The array to be sorted and filtered.
 * @param {Function} comparator - A comparator function used for sorting.
 * @param {string} [query] - A search query used to filter the array.
 * @returns {Array<Object>} - The sorted and filtered array.
 */
function applySortFilter(array, comparator, query) {
  // Stabilize the sorting by adding the index as a secondary sort criterion.
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  // Filter the array if a query is provided.
  if (query) {
    return filter(
      array,
      (_user) =>
        JSON.stringify(_user ?? {})
          .toLowerCase()
          .indexOf(query.toLowerCase()) !== -1
    );
  }
  // Return the sorted array.
  return stabilizedThis.map((el) => el[0]);
}

/**
 * Options for report types used in dropdowns and filtering.
 *
 * @type {Array<{label: string, value: string}>}
 * @property {string} label - The display label of the report type.
 * @property {string} value - The value corresponding to the report type.
 */
const reportTypeOptions = [
  {
    label: 'Commission (Registered)',
    value: 'commissionRegistered',
  },
  {
    label: 'Commission (Unregistered)',
    value: 'commissionUnregistered',
  },
];

/**
 * Component to render the Commission Reports page.
 *
 * This page allows users to:
 * - Filter and view a list of commission reports based on selected criteria.
 * - Download detailed commission reports as files.
 * - Navigate back to the admin dashboard if accessed from there.
 *
 * @component
 * @returns {JSX.Element} The rendered CommissionReportsPage component.
 */
export default function CommissionReportsPage() {
  // States for table functionality and data
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [commissionReports, setCommissionReports] = useState([]);
  const [reportFetch, setReportFetch] = useState(false);
  // Tracks which download button is currently in flight so we can disable it
  // and show a spinner until the report finishes downloading. null = idle.
  const [downloadingKey, setDownloadingKey] = useState(null);

  // Router location and navigation
  const location = useLocation();
  const showSwalAlert = useSwalAlert();

  /**
   * Handles table sorting.
   * @param {React.SyntheticEvent} event - Event from table sort action.
   * @param {string} property - The property to sort by.
   */
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Handles page change for table pagination.
   * @param {React.SyntheticEvent} event - Event from pagination.
   * @param {number} newPage - New page number.
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * Handles change in rows per page for table pagination.
   * @param {React.ChangeEvent<HTMLInputElement>} event - Input change event.
   */
  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  /**
   * Handles filter changes for searching.
   * @param {React.ChangeEvent<HTMLInputElement>} event - Input change event.
   */
  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  // Derived states for filtered and empty rows
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - commissionReports.length) : 0;
  const filteredCommissionReports = applySortFilter(commissionReports, getComparator(order, orderBy), filterName);
  const isNotFound = !filteredCommissionReports.length && !!filterName;

  // Formik for form state management and validation
  const formik = useFormik({
    initialValues: {
      reportType: 'commissionRegistered',
      fromDate: dayjs().subtract(1, 'month').startOf('month'),
      toDate: dayjs().subtract(1, 'month').endOf('month'),
    },
    validationSchema: Yup.object().shape({
      reportType: Yup.string().required('Select the Report Type to view'),
      fromDate: Yup.date()
        .required('From Date is required')
        .typeError('Please enter valid date dd-mm-yyyy format')
        .min(dayjs('01-01-1900'), 'Date must be greater than 01-01-1900')
        .max(dayjs().endOf('day'), 'Date must be equal or before today'),
      toDate: Yup.date()
        .required('To Date is required')
        .typeError('Please enter valid date dd-mm-yyyy format')
        .min(dayjs('01-01-1900'), 'Date must be greater than 01-01-1900')
        .max(dayjs().endOf('day'), 'Date must be equal or before today')
        .test({
          name: 'notBeforeAsFromDate',
          message: 'To Date must be equal or greater than from date',
          test: (toDate, values) => {
            const fromDate = values.parent.fromDate;
            return toDate !== null ? dayjs(fromDate).isSameOrBefore(dayjs(toDate), 'day') : false;
          },
        }),
    }),
    onSubmit: async (values, { setErrors, setStatus, setSubmitting }) => {
      setReportFetch(false);
      axios
        .post('/reports', values)
        .then((response) => {
          if (response.status === 200) {
            setReportFetch(true);
            if (values.reportType === 'commissionUnregistered' || values.reportType === 'commissionRegistered')
              setCommissionReports(response.data.data);
          }
          let icon = 'success';
          if (
            response.data.data.length === 0 &&
            (values.reportType === 'commissionUnregistered' || values.reportType === 'commissionRegistered')
          ) {
            icon = 'error';
          }
          showSwalAlert({
            icon: `${icon}`,
            title: `${response.data.message}`,
            timer: 3000,
          });
        })
        .catch((err) => {
          console.error(err);
          showSwalAlert({
            icon: 'error',
            title: 'Failed to fetch report',
            timer: 3000,
          });
        });
    },
  });

  /**
   * Handles date change for form fields.
   * @param {Date | null} value - New date value.
   * @param {string} name - Field name.
   */
  const handleDateChange = (value, name) => {
    const event = {
      target: {
        name,
        value: value !== null ? dayjs(value) : null,
      },
    };
    formik.handleChange(event);
  };

  /**
   * Generates a detailed commission report and triggers download.
   * @param {string} fromDate - Start date for the report.
   * @param {string} toDate - End date for the report.
   * @param {string} reportType - Type of report.
   * @param {string} [partnerCode=null] - Partner code for the report.
   * @param {string} downloadKey - Unique key identifying the clicked download button.
   */
  const generateCommissionReport = async (fromDate, toDate, reportType, partnerCode = null, downloadKey) => {
    // Guard against duplicate clicks: ignore if a download is already running.
    if (downloadingKey) return;
    setDownloadingKey(downloadKey);
    try {
      const response = await axios.post('/reports/generate-commission-report', {
        partnerCode,
        fromDate,
        toDate,
        reportType,
      });
      if (response.status === 200) {
        const fileKey = response.data.fileKey;
        // Fetch the generated report from the backend, which streams it
        // from S3 server-side (no AWS secret exposed to the browser).
        const blob = await fetchReportBlob(fileKey);
        downloadBlob(
          blob,
          `GWD-Detail-Commission-Report-${dayjs(fromDate).format('DD-MM-YYYY')} to ${dayjs(toDate).format(
            'DD-MM-YYYY'
          )}.pdf`
        );
      }
    } catch (err) {
      console.error('Error downloading file:', err);
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <>
      <PageContainer showBackButton showHelpIcon={false}>
        <Helmet>
          <title> Commission Reports | Get Will Done </title>
        </Helmet>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="title1" gutterBottom>
            Commission Reports
          </Typography>
          <Box style={{ flexGrow: 1, marginLeft: '16px' }}>
            <UserListToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />
          </Box>
        </Stack>
        <form noValidate onSubmit={formik.handleSubmit}>
          <Grid container spacing={1} columnGap={4} sx={{ marginBottom: 1 }}>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography variant="bodyText4">Type</Typography>
                <Dropdown
                  name="reportType"
                  value={formik.values.reportType}
                  onBlur={formik.handleBlur}
                  handleChange={(event) => {
                    setReportFetch(false);
                    setCommissionReports([]);
                    formik.handleChange(event);
                  }}
                  placeholder="Type"
                  options={reportTypeOptions}
                  // dropdownWidth="270px"
                  error={formik.touched.reportType && formik.errors.reportType}
                  helperText={formik.errors.reportType}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography width={'30%'} variant="bodyText4">
                  From Date
                </Typography>
                <FormControl required error={formik.errors.fromDate} fullWidth variant="standard">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <CustomDatePicker
                      value={formik.values.fromDate}
                      name="fromDate"
                      error={formik.errors.fromDate}
                      fullWidth
                      inputFormat={'dd-MM-yyyy'}
                      maxDate={dayjs()}
                      maxDateMessage="Past dates are not allowed"
                      onChange={(value) => handleDateChange(value, 'fromDate')}
                      onBlur={formik.handleBlur}
                      placeholder="From Date(DD-MM-YYYY)"
                    />
                    {true ||
                      (formik.touched.fromDate && formik.errors.fromDate && (
                        <FormHelperText error id={`standard-weight-helper-text-fromDate`}>
                          <Typography variant="suggestiveText">{formik.errors.fromDate || 'error'}</Typography>
                        </FormHelperText>
                      ))}
                  </LocalizationProvider>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography width={'25%'} variant="bodyText4">
                  To Date
                </Typography>
                <FormControl required error={formik.errors.toDate} fullWidth variant="standard">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <CustomDatePicker
                      value={formik.values.toDate}
                      name="toDate"
                      error={formik.errors.toDate}
                      fullWidth
                      inputFormat={'dd-MM-yyyy'}
                      maxDate={dayjs()}
                      onChange={(value) => handleDateChange(value, 'toDate')}
                      onBlur={formik.handleBlur}
                      placeholder="To Date(DD-MM-YYYY)"
                    />
                    {formik.touched.toDate && formik.errors.toDate && (
                      <FormHelperText error id={`standard-weight-helper-text-toDate`}>
                        <Typography variant="suggestiveText">{formik.errors.toDate}</Typography>
                        {formik.errors.toDate}
                      </FormHelperText>
                    )}
                  </LocalizationProvider>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Tooltip title={`Apply`}>
                <ContainedButton1 id={'new-generic'} type="submit">
                  <Typography variant="bodyText3">Apply</Typography>
                </ContainedButton1>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              {formik.errors.submit && (
                <Box sx={{ mt: 3 }}>
                  <FormHelperText error>{formik.errors.submit}</FormHelperText>
                </Box>
              )}
            </Grid>
          </Grid>
        </form>
        <Box>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              {['commissionRegistered', 'commissionUnregistered'].includes(formik.values.reportType) && (
                <>
                  {commissionReports.length > 0 && (
                    <Box sx={{ textAlign: 'right' }}>
                      <Tooltip title="Download Detail Report for All Partners">
                        <span>
                          <Button
                            sx={{ paddingTop: '7px', paddingBottom: '7px' }}
                            disabled={downloadingKey !== null}
                            onClick={() =>
                              generateCommissionReport(
                                formik.values.fromDate,
                                formik.values.toDate,
                                formik.values.reportType,
                                null,
                                'commission-all'
                              )
                            }
                            startIcon={downloadingKey === 'commission-all' ? <CircularProgress size={16} /> : null}
                          >
                            {downloadingKey === 'commission-all' ? (
                              'Downloading...'
                            ) : (
                              <Chip
                                sx={{ cursor: 'pointer' }}
                                label={`Download Detail Report for All Partners`}
                                variant="outlined"
                              />
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={COMMISSION_TABLE_HEAD}
                      rowCount={commissionReports.length}
                      numSelected={selected.length}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {filteredCommissionReports
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row, index) => {
                          const {
                            partnerName,
                            partnerCode,
                            registeredPartner,
                            fromDate,
                            toDate,
                            totalOrderValue,
                            totalPartnerCommission,
                            totalPartnerDiscount,
                          } = row;
                          return (
                            <>
                              <CustomTableRow sx={{ bgcolor: (theme) => 'initial' }} key={index + 1} tabIndex={-1}>
                                <CustomTableCell>{partnerName}</CustomTableCell>
                                <CustomTableCell>{partnerCode}</CustomTableCell>
                                <CustomTableCell>{dayjs(fromDate).format('DD-MM-YYYY')}</CustomTableCell>
                                <CustomTableCell>{dayjs(toDate).format('DD-MM-YYYY')}</CustomTableCell>
                                <CustomTableCell>₹{totalPartnerDiscount ?? 0}</CustomTableCell>
                                <CustomTableCell>₹{totalOrderValue ?? 0}</CustomTableCell>
                                <CustomTableCell>₹{totalPartnerCommission ?? 0}</CustomTableCell>
                                <CustomTableCell>
                                  <Tooltip title="Download Detail Report">
                                    <span>
                                      <Button
                                        fullWidth
                                        sx={{ paddingTop: '7px', paddingBottom: '7px' }}
                                        disabled={downloadingKey !== null}
                                        onClick={() =>
                                          generateCommissionReport(
                                            fromDate,
                                            toDate,
                                            registeredPartner ? 'commissionRegistered' : 'commissionUnregistered',
                                            partnerCode,
                                            `commission-${partnerCode}-${index}`
                                          )
                                        }
                                        startIcon={
                                          downloadingKey === `commission-${partnerCode}-${index}` ? (
                                            <CircularProgress size={16} />
                                          ) : null
                                        }
                                      >
                                        {downloadingKey === `commission-${partnerCode}-${index}` ? (
                                          'Downloading...'
                                        ) : (
                                          <Chip
                                            sx={{ cursor: 'pointer' }}
                                            label={`Download Detail Report`}
                                            variant="outlined"
                                          />
                                        )}
                                      </Button>
                                    </span>
                                  </Tooltip>
                                </CustomTableCell>
                              </CustomTableRow>
                            </>
                          );
                        })}
                      {emptyRows > 0 && (
                        <CustomTableRow style={{ height: 53 * emptyRows }}>
                          <CustomTableCell colSpan={8} />
                        </CustomTableRow>
                      )}
                    </TableBody>

                    {reportFetch && commissionReports.length === 0 && (
                      <TableBody>
                        <CustomTableRow>
                          <CustomTableCell align="center" colSpan={8} sx={{ py: 3 }}>
                            No Partner Commission Found
                          </CustomTableCell>
                        </CustomTableRow>
                      </TableBody>
                    )}
                    {isNotFound && (
                      <TableBody>
                        <CustomTableRow>
                          <CustomTableCell align="center" colSpan={8} sx={{ py: 3 }}>
                            <Paper
                              sx={{
                                textAlign: 'center',
                              }}
                            >
                              <Typography variant="bodyText4" paragraph>
                                Not found
                              </Typography>

                              <Typography variant="smallText">
                                No results found for &nbsp;
                                <strong>&quot;{filterName}&quot;</strong>.
                                <br /> Try checking for typos or using complete words.
                              </Typography>
                            </Paper>
                          </CustomTableCell>
                        </CustomTableRow>
                      </TableBody>
                    )}
                  </Table>
                  <CustomTablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={commissionReports.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
              )}
            </TableContainer>
          </Scrollbar>
        </Box>
      </PageContainer>
    </>
  );
}
